<template>
  <div class="financial-transactions-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-chart-line"></i>
        Comissões dos Cobradores
      </h1>
      <p class="page-subtitle">Consulte e gerencie as comissões dos cobradores</p>
    </div>

    <!-- Loading -->
    <div v-show="loadingCommissions" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>
    </div>

    <!-- Tab content -->
    <div v-show="!loadingCommissions" class="tab-content">
      <!-- Filtros -->
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros de Consulta
          </h3>
        </div>
        
        <div class="filters-content">
          <div class="filters-grid">
            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user-check"></i>
                Cobrador
              </label>
              <select 
                class="form-input" 
                v-model="commissionFilters.collectorId"
                :disabled="isCobrador"
              >
                <option value="">Selecione um cobrador</option>
                <option v-for="collector in filteredCollectors" :key="collector.id" :value="collector.id">
                  {{ collector.name }}
                </option>
              </select>
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-calendar-days"></i>
                Data Início
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="commissionFilters.startDate" 
                placeholder="Data de início"
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-calendar-days"></i>
                Data Fim
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="commissionFilters.endDate" 
                placeholder="Data de fim"
              />
            </div>
          </div>

          <div class="filters-actions">
            <button 
              type="button" 
              class="btn-primary" 
              @click="searchCommissions()"
              :disabled="loadingCommissions"
            >
              <i v-if="loadingCommissions" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-search"></i>
              {{ loadingCommissions ? 'Consultando...' : 'Pesquisar' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Mensagem informativa para usuários com permissões limitadas -->
      <div v-if="!isAdmin" class="permission-info">
        <div class="info-card">
          <i class="fa-solid fa-info-circle"></i>
          <div class="info-content">
            <span v-if="isBanca">
              <strong>Modo BANCA:</strong> Você pode visualizar comissões apenas dos cobradores de suas regiões.
            </span>
            <span v-else-if="isGerente">
              <strong>Modo GERENTE:</strong> Você pode visualizar comissões apenas dos cobradores de suas regiões.
            </span>
            <span v-else-if="isCobrador">
              <strong>Modo COBRADOR:</strong> Você pode visualizar apenas suas próprias comissões.
            </span>
          </div>
        </div>
      </div>

      <!-- Resumo Compacto de Comissões -->
      <div v-show="commissionFilters.collectorId" class="commission-summary-compact">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-coins"></i>
            Resumo de Comissões - {{ selectedCollectorName }}
          </h3>
          <div class="summary-status">
            <span v-if="collectorSummary" 
                  :class="['status-badge', (collectorSummary.totalPendingCommission || 0) > 0 ? 'status-pending' : 'status-paid']">
              <i class="fa-solid" :class="(collectorSummary.totalPendingCommission || 0) > 0 ? 'fa-clock' : 'fa-check'"></i>
              {{ (collectorSummary.totalPendingCommission || 0) > 0 ? 'Pendente' : 'Em dia' }}
            </span>
            <span v-else-if="commissionFilters.collectorId" class="status-badge status-loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              Carregando...
            </span>
            <button 
              v-if="canMakePayments && collectorSummary && collectorSummary.totalPendingCommission > 0"
              class="btn-payment-summary" 
              @click="openPaymentModal()"
              :disabled="!collectorSummary || collectorSummary.totalPendingCommission <= 0"
            >
              <i class="fa-solid fa-money-bill-wave"></i>
              Pagar
            </button>
          </div>
        </div>
        
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-icon">
              <i class="fa-solid fa-chart-line"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Comissão Bruta Total</div>
              <div class="summary-value">{{ setCurrencyString(totalGrossCommissions) }}</div>
            </div>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon">
              <i class="fa-solid fa-percentage"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Comissão Líquida Total</div>
              <div class="summary-value">{{ setCurrencyString(totalNetCommissions) }}</div>
            </div>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon">
              <i class="fa-solid fa-clock"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Comissões Pendentes</div>
              <div class="summary-value pending">{{ setCurrencyString(collectorSummary ? collectorSummary.totalPendingCommission : pendingCommissions) }}</div>
            </div>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon">
              <i class="fa-solid fa-check-circle"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Último Pagamento</div>
              <div class="summary-value">{{ formatLastPaymentDate() }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabela de Histórico de Comissões (apenas pendentes — pagas ficam em Registros de Pagamentos) -->
      <div v-show="pendingCommissionsList.length > 0" class="results-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-table"></i>
            Histórico de Comissões
            <span class="table-count">({{ pendingCommissionsList.length }} registro(s))</span>
          </h3>
        </div>
        
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Cobrador</th>
                <th>Comissão Bruta</th>
                <th>Comissão Líquida</th>
                <th>Total</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="commission in pendingCommissionsList" 
                :key="commission.id"
                class="data-row"
              >
                <td>
                  <div class="date-cell">
                    <i class="fa-solid fa-calendar-day"></i>
                    <span>{{ formatDate(commission.date) }}</span>
                  </div>
                </td>
                <td>
                  <div class="seller-cell">
                    <i class="fa-solid fa-user"></i>
                    <span>{{ commission.collectorName }}</span>
                  </div>
                </td>
                <td>
                  <span :class="['currency-value', { 'currency-negative': commission.grossCommissionAmount < 0, 'currency-positive': commission.grossCommissionAmount >= 0 }]">{{ setCurrencyString(commission.grossCommissionAmount) }}</span>
                </td>
                <td>
                  <span :class="['currency-value', { 'currency-negative': commission.netCommissionAmount < 0, 'currency-positive': commission.netCommissionAmount >= 0 }]">{{ setCurrencyString(commission.netCommissionAmount) }}</span>
                </td>
                <td>
                  <span class="currency-value currency-positive total-amount">{{ setCurrencyString(commission.grossCommissionAmount + commission.netCommissionAmount) }}</span>
                </td>
                <td>
                  <span :class="['type-badge', commission.isPaid ? 'badge-success' : 'badge-danger']">
                    <i class="fa-solid" :class="commission.isPaid ? 'fa-check-circle' : 'fa-clock'"></i>
                    {{ commission.isPaid ? 'Pago' : 'Pendente' }}
                  </span>
                </td>
                <td>
                  <button 
                    v-if="!commission.isPaid && canMakePayments"
                    class="btn-pay-commission" 
                    @click="paySpecificCommission(commission)"
                    :disabled="savingPayment"
                  >
                    <i v-if="savingPayment" class="fa-solid fa-spinner fa-spin"></i>
                    <i v-else class="fa-solid fa-money-bill-wave"></i>
                    {{ savingPayment ? 'Pagando...' : 'Pagar' }}
                  </button>
                  <span v-else-if="commission.isPaid" class="text-muted">-</span>
                  <span v-else class="text-muted">Sem permissão</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabela de Registros de Pagamentos -->
      <div v-show="commissionFilters.collectorId" class="results-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-receipt"></i>
            Registros de Pagamentos Realizados
            <span class="table-count">({{ paymentPagination.totalItems }} registro(s) total)</span>
          </h3>
        </div>
        
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Data Pagamento</th>
                <th>Período</th>
                <th>Valor Comissão</th>
                <th>Pago Por</th>
                <th>Observações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="payment in paginatedPaymentHistory" 
                :key="payment.id"
                class="data-row payment-row"
              >
                <td>
                  <div class="payment-date-cell">
                    <i class="fa-solid fa-calendar-check"></i>
                    <span>{{ formatDate(payment.paymentDate) }}</span>
                  </div>
                </td>
                <td>
                  <span class="payment-period">
                    {{ formatDateShort(payment.paymentPeriodStart) }} - {{ formatDateShort(payment.paymentPeriodEnd) }}
                  </span>
                </td>
                <td>
                  <span class="currency-value currency-positive total-amount">{{ setCurrencyString(payment.totalAmount) }}</span>
                </td>
                <td>
                  <span class="paid-by">{{ payment.paidByUserName }}</span>
                </td>
                <td>
                  <span class="payment-notes">{{ payment.notes || '-' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Paginação -->
        <div v-if="paymentPagination.totalPages > 1" class="pagination-container">
          <div class="pagination-info">
            Mostrando {{ ((paymentPagination.currentPage - 1) * paymentPagination.itemsPerPage) + 1 }} 
            até {{ Math.min(paymentPagination.currentPage * paymentPagination.itemsPerPage, paymentPagination.totalItems) }} 
            de {{ paymentPagination.totalItems }} registros
          </div>
          
          <div class="pagination-controls">
            <button 
              class="pagination-btn" 
              @click="previousPaymentPage" 
              :disabled="paymentPagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
              Anterior
            </button>
            
            <div class="pagination-numbers">
              <button 
                v-for="page in Math.min(5, paymentPagination.totalPages)" 
                :key="page"
                class="pagination-number" 
                :class="{ active: page === paymentPagination.currentPage }"
                @click="goToPaymentPage(page)"
              >
                {{ page }}
              </button>
              
              <span v-if="paymentPagination.totalPages > 5" class="pagination-ellipsis">...</span>
              
              <button 
                v-if="paymentPagination.totalPages > 5 && paymentPagination.currentPage < paymentPagination.totalPages - 2"
                class="pagination-number"
                @click="goToPaymentPage(paymentPagination.totalPages)"
              >
                {{ paymentPagination.totalPages }}
              </button>
            </div>
            
            <button 
              class="pagination-btn" 
              @click="nextPaymentPage" 
              :disabled="paymentPagination.currentPage === paymentPagination.totalPages"
            >
              Próxima
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
        
        <!-- Estado vazio para pagamentos -->
        <div v-if="paymentHistory.length === 0 && commissionFilters.collectorId" class="empty-payments-state">
          <div class="empty-state-content">
            <i class="fa-solid fa-receipt"></i>
            <h4>Nenhum pagamento encontrado</h4>
            <p>Este cobrador ainda não possui registros de pagamentos realizados</p>
          </div>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-show="pendingCommissionsList.length === 0 && commissionFilters.collectorId && !loadingCommissions" class="empty-state">
        <div class="empty-state-content">
          <i class="fa-solid fa-chart-line"></i>
          <h3>Nenhuma comissão pendente</h3>
          <p>Não há comissões pendentes para os filtros selecionados (pagas aparecem em Registros de Pagamentos)</p>
        </div>
      </div>
    </div>

    <!-- Modal de Pagamento -->
    <div v-if="showPaymentModal" class="modal-overlay" @click="closePaymentModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-money-bill-wave"></i>
            Registrar Pagamento - {{ selectedCollectorName }}
          </h3>
          <button class="modal-close" @click="closePaymentModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="payment-form">
            <div class="form-grid">
              <div class="input-group">
                <label class="input-label required">
                  <i class="fa-solid fa-calendar-start"></i>
                  Data Início do Período
                </label>
                <input 
                  type="datetime-local" 
                  v-model="paymentForm.paymentPeriodStart" 
                  class="form-input"
                />
              </div>
              
              <div class="input-group">
                <label class="input-label required">
                  <i class="fa-solid fa-calendar-end"></i>
                  Data Fim do Período
                </label>
                <input 
                  type="datetime-local" 
                  v-model="paymentForm.paymentPeriodEnd" 
                  class="form-input"
                />
              </div>
              
                          <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-percentage"></i>
                Valor Total da Comissão (Bruta + Líquida)
              </label>
              <input 
                type="number" 
                step="0.01" 
                v-model="paymentForm.commissionAmount" 
                class="form-input"
                placeholder="0,00"
                readonly
              />
            </div>
            
            <div class="input-group full-width">
                <label class="input-label">
                  <i class="fa-solid fa-note-sticky"></i>
                  Observações
                </label>
                <textarea 
                  v-model="paymentForm.notes" 
                  class="form-input"
                  rows="3"
                  placeholder="Observações sobre o pagamento..."
                ></textarea>
              </div>
              
              <div class="payment-total">
                <span class="total-label">Total a Pagar:</span>
                <span class="total-value">{{ setCurrencyString(totalPaymentAmount) }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            @click="closePaymentModal"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            @click="processPayment"
            :disabled="savingPayment || !isPaymentFormValid"
          >
            <i v-if="savingPayment" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ savingPayment ? 'Processando...' : 'Registrar Pagamento' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="confirmModalData.title"
      :message="confirmModalData.message"
      :details="confirmModalData.details"
      :type="confirmModalData.type"
      :loading="savingPayment"
      confirmText="Confirmar Pagamento"
      cancelText="Cancelar"
      loadingText="Processando pagamento..."
      @confirm="handleConfirmPayment"
      @cancel="handleCancelPayment"
    />
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import { ROLES } from "@/services/permissions";
import ConfirmModal from "@/components/utils/ConfirmModal.vue";

export default {
  name: "ViewCollectorCommissions",
  components: { ConfirmModal },
  data: () => ({
    loadingCommissions: true,
    collectors: [],
    commissions: [],
    paymentHistory: [],
    collectorSummary: null,
    commissionFilters: {
      collectorId: "",
      startDate: "",
      endDate: ""
    },
    // Paginação para histórico de pagamentos
    paymentPagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0
    },
    // Modal de pagamento
    showPaymentModal: false,
    paymentForm: {
      paymentPeriodStart: '',
      paymentPeriodEnd: '',
      commissionAmount: 0,
      notes: ''
    },
    savingPayment: false,
    // Modal de confirmação
    showConfirmModal: false,
    confirmModalData: {
      title: 'Confirmação',
      message: '',
      details: null,
      type: 'primary',
      action: null
    },
    // Controle de usuário e permissões
    currentUser: null,
    userResponsibleRegions: [],
    bancas: [],
    regions: [],
  }),
  computed: {
    // Comissões ainda não pagas (listagem principal)
    pendingCommissionsList() {
      if (!Array.isArray(this.commissions)) return [];
      return this.commissions.filter((c) => !c.isPaid);
    },
    totalGrossCommissions() {
      return this.pendingCommissionsList.reduce(
        (sum, commission) => sum + (commission.grossCommissionAmount || 0),
        0,
      );
    },
    totalNetCommissions() {
      return this.pendingCommissionsList.reduce(
        (sum, commission) => sum + (commission.netCommissionAmount || 0),
        0,
      );
    },
    paidCommissions() {
      if (!Array.isArray(this.commissions)) return 0;
      return this.commissions
        .filter((commission) => commission.isPaid)
        .reduce((sum, commission) => sum + (commission.grossCommissionAmount || 0), 0);
    },
    pendingCommissions() {
      return this.collectorSummary ? this.collectorSummary.totalPendingCommission : 0;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    canMakePayments() {
      // Apenas ADMIN, BANCA e GERENTE podem fazer pagamentos
      return this.isAdmin || this.isBanca || this.isGerente;
    },

    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    selectedCollectorName() {
      const collector = this.filteredCollectors.find(c => c.id === this.commissionFilters.collectorId);
      return collector ? collector.name : '';
    },

    totalPaymentAmount() {
      return parseFloat(this.paymentForm.commissionAmount) || 0;
    },

    isPaymentFormValid() {
      return this.paymentForm.paymentPeriodStart && 
             this.paymentForm.paymentPeriodEnd && 
             this.paymentForm.commissionAmount > 0 &&
             this.commissionFilters.collectorId;
    },

    filteredCollectors() {
      let collectors = this.collectors;
      
      // Aplicar filtro baseado no role do usuário
      if (this.isCobrador) {
        // COBRADOR só vê a si mesmo
        collectors = collectors.filter(collector => 
          collector.id === this.currentUser.id
        );
      } else if (!this.isAdmin) {
        // BANCA e GERENTE veem apenas cobradores de suas regiões
        collectors = collectors.filter(collector => {
          const hasPermission = this.userResponsibleRegions.includes(collector.regionId) ||
            (collector.additionalRegions && collector.additionalRegions.some(region => 
              this.userResponsibleRegions.includes(region.id)
            ));
          
          return hasPermission;
        });
        
        // Para usuários não-admin, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          collectors = collectors.filter(collector => {
            const matches = String(collector.regionId) === String(this.currentSelectedRegion.id);
            return matches;
          });
        }
      } else if (this.isAdmin) {
        // ADMIN: aplicar filtro por banca selecionada primeiro
        if (this.currentSelectedBanca) {
          collectors = collectors.filter(collector => {
            const matches = String(collector.bancaId) === String(this.currentSelectedBanca.id);
            return matches;
          });
        }
        
        // Em seguida, aplicar filtro por região apenas se houver região selecionada
        if (this.currentSelectedRegion) {
          collectors = collectors.filter(collector => {
            const matches = String(collector.regionId) === String(this.currentSelectedRegion.id);
            return matches;
          });
        }
        // Se currentSelectedRegion é null, não aplica filtro de região (mostra todos)
      }
      
      return collectors;
    },

    paginatedPaymentHistory() {
      if (!Array.isArray(this.paymentHistory)) return [];
      
      const startIndex = (this.paymentPagination.currentPage - 1) * this.paymentPagination.itemsPerPage;
      const endIndex = startIndex + this.paymentPagination.itemsPerPage;
      
      return this.paymentHistory.slice(startIndex, endIndex);
    }
  },
  async mounted() {
    this.loadingCommissions = true;
    try {
      await this.loadCurrentUser();
      await this.initializeFilters();
      
      // Carregar bancas primeiro
      await this.loadBancas();
      
      // Para usuários BANCA, complementar regiões responsáveis com regiões das bancas
      if (this.isBanca) {
        await this.loadBancaRegions();
      }
      
      await this.loadCollectors();
      
      // Se for COBRADOR, auto-selecionar ele mesmo
      if (this.isCobrador && this.filteredCollectors.length > 0) {
        this.commissionFilters.collectorId = this.filteredCollectors[0].id;
      }
    } catch (error) {
      toast.error(handleErrorMessage(error));
    } finally {
      this.loadingCommissions = false;
    }
  },
  watch: {
    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Se a região mudou, limpar o cobrador selecionado para forçar uma nova seleção
        this.commissionFilters.collectorId = "";
        this.commissions = [];
        this.paymentHistory = [];
        this.collectorSummary = null;
        
        // Se for COBRADOR, auto-selecionar ele mesmo novamente
        this.$nextTick(() => {
          if (this.isCobrador && this.filteredCollectors.length > 0) {
            this.commissionFilters.collectorId = this.filteredCollectors[0].id;
          }
        });
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Se a banca mudou, limpar o cobrador selecionado para forçar uma nova seleção
        this.commissionFilters.collectorId = "";
        this.commissions = [];
        this.paymentHistory = [];
        this.collectorSummary = null;
        
        // Se for COBRADOR, auto-selecionar ele mesmo novamente
        this.$nextTick(() => {
          if (this.isCobrador && this.filteredCollectors.length > 0) {
            this.commissionFilters.collectorId = this.filteredCollectors[0].id;
          }
        });
      },
      deep: true
    },

    'commissionFilters.collectorId': {
      handler(newCollectorId) {
        if (newCollectorId) {
          this.loadCollectorSummary();
          this.loadPaymentHistory();
        } else {
          this.collectorSummary = null;
          this.paymentHistory = [];
        }
      }
    }
  },
  methods: {
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (this.currentUser.role !== 0) {
            await this.loadUserResponsibleRegions();
          }
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        
        // Buscar bancas disponíveis para o usuário BANCA
        const bancasForUser = this.bancas.filter(banca => 
          banca.responsibleUserIds && banca.responsibleUserIds.includes(this.currentUser.id)
        );
        
        for (const banca of bancasForUser) {
          if (banca.regionIds && Array.isArray(banca.regionIds)) {
            // Adicionar todas as regiões desta banca às regiões responsáveis
            banca.regionIds.forEach(regionId => {
              if (!this.userResponsibleRegions.includes(regionId)) {
                this.userResponsibleRegions.push(regionId);
              }
            });
          }
        }
      } catch (error) {
        // Ignorar erro
      }
    },

    async initializeFilters() {
      // Configurar datas padrão
      const today = new Date();
      const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      this.commissionFilters.endDate = _fmt(today);

      // Data de início padrão: último mês
      const lastMonth = new Date(today);
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      this.commissionFilters.startDate = _fmt(lastMonth);
    },

    async adjustFiltersBasedOnLastPayment() {
      // Ajustar filtros baseado no último pagamento quando cobrador for selecionado
      if (!this.collectorSummary || !this.collectorSummary.lastPaymentPeriodEnd) {
        return; // Se não há último pagamento, manter filtros padrão
      }

      const lastPaymentEnd = new Date(this.collectorSummary.lastPaymentPeriodEnd);
      const today = new Date();
      const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      // Data de início: 1 segundo após o último pagamento
      const startDate = new Date(lastPaymentEnd);
      startDate.setSeconds(startDate.getSeconds() + 1);

      // Ajustar filtros apenas se a data do último pagamento for mais recente que a data atual de filtro
      const currentStartDate = new Date(this.commissionFilters.startDate);
      if (startDate > currentStartDate) {
        this.commissionFilters.startDate = _fmt(startDate);

        // Atualizar data final para hoje
        this.commissionFilters.endDate = _fmt(today);

        // Recarregar comissões automaticamente com os novos filtros
        if (this.commissionFilters.collectorId) {
          await this.searchCommissions();
        }
      }
    },

    async loadBancas() {
      const response = await mainService.searchBancas({ isActive: true });
      this.bancas = response.data || [];
    },

    async loadRegions() {
      const response = await mainService.searchRegions();
      this.regions = response.data || [];
    },

    async loadCollectors() {
      try {
        // PRIMEIRO: Carregar bancas e regiões se ainda não foram carregadas
        if (!this.bancas || this.bancas.length === 0) {
          await this.loadBancas();
        }
        
        if (!this.regions || this.regions.length === 0) {
          await this.loadRegions();
        }
        
        // SEGUNDO: Buscar cobradores
        const response = await mainService.searchCollectors();
        
        // TERCEIRO: Mapear dados dos cobradores com bancaId
        this.collectors = response.data.map(collector => {
          // Buscar a região do cobrador para obter a banca
          const collectorRegion = this.regions.find(r => String(r.id) === String(collector.regionId));
          const collectorBanca = collectorRegion ? this.bancas.find(b => String(b.id) === String(collectorRegion.bancaId)) : null;
          
          return {
            id: collector.id,
            name: collector.name,
            username: collector.username,
            phone: collector.phone,
            regionId: collector.regionId,
            regionName: collector.regionName,
            bancaId: collectorBanca?.id || null, // ID da banca baseado na região
            bancaName: collectorBanca?.name || '-', // Nome da banca baseado na região
            active: collector.isActive
          };
        });

        // QUARTO: Para filtros não-admin, carregar regiões adicionais de cada cobrador
        if (!this.isAdmin) {
          await this.loadAdditionalRegionsForCollectors();
        }
        
      } catch (error) {
        toast.error("Erro ao carregar cobradores");
      }
    },

    async loadAdditionalRegionsForCollectors() {
      // Carregar regiões adicionais para cada cobrador para o filtro funcionar corretamente
      const promises = this.collectors.map(async (collector) => {
        try {
          const response = await mainService.getUserRegions(collector.id);
          collector.additionalRegions = response.data || [];
        } catch (error) {
          collector.additionalRegions = [];
        }
      });

      await Promise.all(promises);
    },

    async loadCollectorSummary() {
      if (!this.commissionFilters.collectorId) return;

      try {
        const response = await mainService.getCollectorPaymentSummary(this.commissionFilters.collectorId);
        this.collectorSummary = response.data;
        
        // Ajustar filtros baseado no último pagamento
        await this.adjustFiltersBasedOnLastPayment();
      } catch (error) {
        this.collectorSummary = null;
        
        // Só mostrar erro se não for 500 (erro interno comum)
        if (error.response && error.response.status !== 500) {
          toast.error(`Erro ao carregar resumo: ${handleErrorMessage(error)}`);
        }
      }
    },

    async loadPaymentHistory() {
      if (!this.commissionFilters.collectorId) {
        this.paymentHistory = [];
        this.updatePaymentPagination();
        return;
      }

      try {
        const response = await mainService.getCollectorPayments(this.commissionFilters.collectorId);
        this.paymentHistory = response.data.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
        this.updatePaymentPagination();
      } catch (error) {
        this.paymentHistory = [];
        this.updatePaymentPagination();
        
        // Só mostrar erro se não for 404 (nenhum pagamento encontrado) ou 500 (erro interno comum)
        if (error.response && error.response.status !== 404 && error.response.status !== 500) {
          toast.error(`Erro ao carregar pagamentos: ${handleErrorMessage(error)}`);
        }
      }
    },

    async searchCommissions() {
      if (!this.commissionFilters.collectorId) {
        toast.warning("Selecione um cobrador para consultar as comissões!");
        return;
      }
      if (!this.commissionFilters.startDate) {
        toast.warning("Selecione a data inicial!");
        return;
      }
      if (!this.commissionFilters.endDate) {
        toast.warning("Selecione a data final!");
        return;
      }

      this.loadingCommissions = true;
      try {
        const dateBeginStr = this.commissionFilters.startDate + "T00:00:00";
        const dateEndStr = this.commissionFilters.endDate + "T23:59:59";
        
        const request = {
          collectorId: this.commissionFilters.collectorId,
          startDate: dateBeginStr,
          endDate: dateEndStr
        };
        
        const response = await mainService.searchCollectorCommissions(request);
        // Garantir que sempre tenhamos um array
        this.commissions = Array.isArray(response.data) ? response.data : [];
        
        if (this.commissions.length === 0) {
          toast.info("Nenhuma comissão encontrada para o cobrador no período selecionado.");
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.commissions = [];
      } finally {
        this.loadingCommissions = false;
      }
    },

    openPaymentModal() {
      if (!this.canMakePayments) {
        toast.error("Você não tem permissão para registrar pagamentos!");
        return;
      }

      if (!this.collectorSummary) {
        toast.error("Carregue os dados do cobrador primeiro!");
        return;
      }
      
      // Pré-preencher período baseado no último pagamento
      const now = new Date();
      
      // Data de início: último pagamento + 1 segundo, ou início do mês se não há pagamento
      let startDate;
      if (this.collectorSummary.lastPaymentPeriodEnd) {
        startDate = new Date(this.collectorSummary.lastPaymentPeriodEnd);
        startDate.setSeconds(startDate.getSeconds() + 1);
      } else {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      
      // Data de fim: agora
      const endDate = new Date(now);
      
      // Formatar para datetime-local
      this.paymentForm.paymentPeriodStart = this.formatDateTimeLocal(startDate);
      this.paymentForm.paymentPeriodEnd = this.formatDateTimeLocal(endDate);
      // Calcular comissão total (bruta + líquida)
      this.paymentForm.commissionAmount = this.totalGrossCommissions + this.totalNetCommissions;
      this.paymentForm.notes = '';
      
      this.showPaymentModal = true;
    },

    closePaymentModal() {
      this.showPaymentModal = false;
      this.paymentForm = {
        paymentPeriodStart: '',
        paymentPeriodEnd: '',
        commissionAmount: 0,
        notes: ''
      };
    },

    formatDateTimeLocal(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    async processPayment() {
      if (!this.isPaymentFormValid) {
        toast.error("Preencha todos os campos obrigatórios!");
        return;
      }

      this.savingPayment = true;

      try {
                    const request = {
              collectorId: this.commissionFilters.collectorId,
              paymentPeriodStart: new Date(this.paymentForm.paymentPeriodStart).toISOString(),
              paymentPeriodEnd: new Date(this.paymentForm.paymentPeriodEnd).toISOString(),
              commissionAmount: parseFloat(this.paymentForm.commissionAmount),
              bonusAmount: 0,
              notes: this.paymentForm.notes || null,
              paidByUserId: this.currentUser?.id
            };

        await mainService.createCollectorPayment(request);
        
        toast.success("Pagamento registrado com sucesso!");
        
        // Recarregar dados
        await this.loadCollectorSummary();
        await this.loadPaymentHistory();
        
        // Recarregar comissões para atualizar status
        await this.searchCommissions();
        
        this.closePaymentModal();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.savingPayment = false;
      }
    },

    async paySpecificCommission(commission) {
      if (!this.canMakePayments) {
        toast.error("Você não tem permissão para registrar pagamentos!");
        return;
      }

      // Configurar modal de confirmação
      this.confirmModalData = {
        title: 'Confirmar Pagamento',
        message: `Confirma o pagamento da comissão do cobrador ${commission.collectorName}?`,
        details: {
          'Data da Comissão': this.formatDate(commission.date),
          'Comissão Bruta': this.setCurrencyString(commission.grossCommissionAmount),
          'Comissão Líquida': this.setCurrencyString(commission.netCommissionAmount),
          'Valor Total': this.setCurrencyString(commission.grossCommissionAmount + commission.netCommissionAmount),
          'Cobrador': commission.collectorName
        },
        type: 'success',
                 action: async () => {
          try {
            // Criar período de pagamento baseado na data da comissão
            const commissionDate = new Date(commission.date);
            const startOfDay = new Date(commissionDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            const endOfDay = new Date(commissionDate);
            endOfDay.setHours(23, 59, 59, 999);

            // Calcular valor total da comissão
            const totalCommission = (commission.grossCommissionAmount || 0) + (commission.netCommissionAmount || 0);
            
            const request = {
              collectorId: this.commissionFilters.collectorId,
              paymentPeriodStart: startOfDay.toISOString(),
              paymentPeriodEnd: endOfDay.toISOString(),
              commissionAmount: totalCommission,
              bonusAmount: 0,
              notes: `Pagamento da comissão do dia ${this.formatDate(commission.date)}`,
              paidByUserId: this.currentUser?.id
            };

            await mainService.createCollectorPayment(request);
            
            toast.success("Pagamento registrado com sucesso!");
            
            // Recarregar dados
            await this.loadCollectorSummary();
            await this.loadPaymentHistory();
            
            // Recarregar comissões para atualizar status
            await this.searchCommissions();
            
                      } catch (error) {
              toast.error(`Erro ao registrar pagamento: ${handleErrorMessage(error)}`);
              throw error; // Re-throw para manter o modal aberto se necessário
            }
        }
      };

      this.showConfirmModal = true;
    },

    async handleConfirmPayment() {
      if (this.confirmModalData.action) {
        this.savingPayment = true;
        try {
          await this.confirmModalData.action();
          this.showConfirmModal = false;
        } catch (error) {
          // Erro já tratado na action
        } finally {
          this.savingPayment = false;
        }
      }
    },

    handleCancelPayment() {
      this.showConfirmModal = false;
      this.confirmModalData = {
        title: 'Confirmação',
        message: '',
        details: null,
        type: 'primary',
        action: null
      };
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return jsFunctions.setDateDisplay(dateString);
    },

    formatDateShort(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    },

    formatLastPaymentDate() {
      if (!this.collectorSummary || !this.collectorSummary.lastPaymentDate) return 'Nunca';
      const date = new Date(this.collectorSummary.lastPaymentDate);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },

    updatePaymentPagination() {
      this.paymentPagination.totalItems = this.paymentHistory.length;
      this.paymentPagination.totalPages = Math.ceil(this.paymentPagination.totalItems / this.paymentPagination.itemsPerPage);
      
      // Se a página atual é maior que o total de páginas, volta para a primeira
      if (this.paymentPagination.currentPage > this.paymentPagination.totalPages && this.paymentPagination.totalPages > 0) {
        this.paymentPagination.currentPage = 1;
      }
    },

    goToPaymentPage(page) {
      if (page >= 1 && page <= this.paymentPagination.totalPages) {
        this.paymentPagination.currentPage = page;
      }
    },

    previousPaymentPage() {
      if (this.paymentPagination.currentPage > 1) {
        this.paymentPagination.currentPage--;
      }
    },

    nextPaymentPage() {
      if (this.paymentPagination.currentPage < this.paymentPagination.totalPages) {
        this.paymentPagination.currentPage++;
      }
    },

    setCurrencyString(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'R$ 0,00';
      }
      return jsFunctions.setCurrencyString(value);
    }
  }
};
</script>

<style scoped>
/* Container principal */
.financial-transactions-container {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Cabeçalho da página */
.page-header {
  margin-bottom: 2rem;
}

.page-title {
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  align-items: center;
  gap: 0.75rem;
}

.page-title i {
  color: var(--accent-color);
  font-size: 1.75rem;
}

.page-subtitle {
  color: var(--text-secondary);
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary);
  font-size: 1.1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
  color: var(--accent-color);
}

/* Tab content */
.tab-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Cards */
.filters-card,
.results-card,
.commission-summary-compact {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--secondary-color), #f1f1f1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

[data-theme="dark"] .card-header {
  background: linear-gradient(135deg, var(--secondary-color), #17171a);
}

.card-title {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title i {
  color: var(--accent-color);
  font-size: 1.125rem;
}

.table-count {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 400;
}

/* Resumo Compacto */
.summary-status {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-pending {
  background: rgba(255, 193, 7, 0.1);
  color: #f59e0b;
}

.status-paid {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.status-loading {
  background: rgba(156, 163, 175, 0.1);
  color: #6b7280;
}

.btn-payment-summary {
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-payment-summary:hover:not(:disabled) {
  background: #059669;
}

.btn-payment-summary:disabled {
  background: #6b7280;
  cursor: not-allowed;
}

.btn-pay-commission {
  background: #10b981;
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-pay-commission:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-pay-commission:disabled {
  background: #6b7280;
  cursor: not-allowed;
  transform: none;
}

.text-muted {
  color: var(--text-muted);
  font-size: 0.85rem;
  font-style: italic;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--secondary-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
}

.summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.summary-content {
  flex: 1;
}

.summary-label {
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.summary-value {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  font-family: 'Consolas', 'Monaco', monospace;
}

.summary-value.pending {
  color: #f59e0b;
}

/* Filtros */
.filters-content {
  padding: 1.5rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.input-label i {
  color: var(--accent-color);
  font-size: 0.9rem;
}

.input-label.required::after {
  content: " *";
  color: #dc3545;
}

.form-input {
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-color);
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 0.95rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  width: 100%;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 0.2rem var(--shadow);
}

.form-input::placeholder {
  color: var(--text-muted);
}

.filters-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

/* Buttons */
.btn-primary {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  background: var(--accent-color);
  color: white;
  box-shadow: 0 3px 8px rgba(74, 144, 226, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  padding: 0.875rem 1.5rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  background: var(--secondary-color);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--border-color);
}

/* Table */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Células da tabela */
.date-cell,
.seller-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
}

.date-cell i,
.seller-cell i {
  color: var(--accent-color);
  font-size: 0.9rem;
}

.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.currency-positive {
  color: #10b981 !important;
}

.currency-negative {
  color: #ef4444 !important;
}

.type-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.badge-success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.badge-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Células específicas da tabela de pagamentos */
.payment-date-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
}

.payment-date-cell i {
  color: #10b981;
  font-size: 0.9rem;
}

.payment-date {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.payment-period {
  font-size: 0.85rem;
  color: var(--text-secondary);
  background: rgba(96, 165, 250, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
}

.payment-row {
  background: rgba(16, 185, 129, 0.02);
}

.payment-row:hover {
  background: rgba(16, 185, 129, 0.05);
}

.total-amount {
  font-weight: 600;
  font-size: 1.05rem;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.paid-by {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.payment-notes {
  font-size: 0.85rem;
  color: var(--text-muted);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--card-background);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-title i {
  color: var(--accent-color);
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

/* Formulário de pagamento */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.payment-total {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--secondary-color);
  border: 2px solid var(--accent-color);
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-top: 1rem;
}

.total-label {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.total-value {
  color: var(--accent-color);
  font-size: 1.3rem;
  font-weight: 600;
  font-family: 'Consolas', 'Monaco', monospace;
}

/* Mensagem de permissão */
.permission-info {
  padding: 7px;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  margin-bottom: 20px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Estado vazio */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.empty-state-content {
  text-align: center;
  color: var(--text-secondary);
}

.empty-state-content i {
  font-size: 4rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.empty-state-content h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
}

.empty-state-content p {
  margin: 0;
  font-size: 1rem;
  color: var(--text-secondary);
}

/* Responsividade */
@media (max-width: 768px) {
  .financial-transactions-container {
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modern-table {
    font-size: 0.8rem;
  }

  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
  }

  .summary-status {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .card-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .modern-table th,
  .modern-table td {
    padding: 0.375rem 0.5rem;
  }
}

/* Paginação */
.pagination-container {
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
  background: var(--secondary-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn {
  background: var(--card-background);
  border: 2px solid var(--border-color);
  color: var(--text-primary);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--border-color);
}

.pagination-numbers {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-number {
  background: var(--card-background);
  border: 2px solid var(--border-color);
  color: var(--text-primary);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 40px;
  text-align: center;
}

.pagination-number:hover {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.pagination-number.active {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
  box-shadow: 0 2px 6px rgba(74, 144, 226, 0.3);
}

.pagination-ellipsis {
  color: var(--text-secondary);
  padding: 0 0.5rem;
  font-weight: 600;
}

.empty-payments-state {
  padding: 3rem 1.5rem;
  text-align: center;
  background: var(--secondary-color);
  border-top: 1px solid var(--border-color);
}

.empty-payments-state .empty-state-content {
  color: var(--text-secondary);
}

.empty-payments-state .empty-state-content i {
  font-size: 3rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.empty-payments-state .empty-state-content h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
}

.empty-payments-state .empty-state-content p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--text-secondary);
}

/* Responsividade para paginação */
@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }

  .pagination-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }

  .pagination-number {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    min-width: 35px;
  }
}
</style>